import { useMemo } from "react";
import { Text, View } from "react-native";
import { ArrowRight } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useLocale, useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { HomepageGender, HomepageGoal } from "../../homepage.types";
import type { Pathway, PlanAction, PlanStep, StepStatus } from "../plan.types";
import { getJourneyForState } from "../journeys";
import { CardSurface, CompactActionButton } from "@louise/moon-ui";
import { EggFreezingClinicTimelineStep } from "./EggFreezingPrepTimelineRemainingSteps";
import { FindEmbryoCareStep } from "./EmbryoFreezingClinicStep";
import { OptimizeInputsStep } from "./EmbryoFreezingOptimizationStep";
import { StartEmbryoCycleStep } from "./EmbryoFreezingStartCycleStep";
import {
  UnderstandEmbryoFreezingStep,
  type PreCycleBiologyVariant,
} from "./EmbryoFreezingUnderstandStep";
import { EggFreezingKnowYourNumbersTimelineStepSectionCard } from "./EggFreezingKnowYourNumbersCard";
import { ExplorerBiologyOptionsCard } from "./ExplorerBiologyOptionsCard";
import { ExplorerJourneyHandoffCard } from "./ExplorerJourneyHandoffCard";
import { NeutralStepPill } from "./NeutralStepPill";
import { PreCycleHome, type PreCycleHomeSection } from "./PreCycleHome";
import type { PreCyclePrepStep } from "./PreCycleHeroCard";
import type { HeroCycleEnding } from "./hero/Hero";
import { StepperSection } from "./Stepper";

export type GuidedPreCycleConceptVariant =
  | "setup"
  | "learn-explorer"
  | "trying-naturally"
  | "ivf-precycle"
  | "iui-precycle"
  | "amab-family-building-precycle";

type JourneySource = {
  gender: HomepageGender;
  goal: HomepageGoal;
  pathway: Pathway;
};

type CustomSection = {
  id: string;
  stepLabel: string;
  title: string;
  body: string;
  bullets: string[];
  action: PlanAction;
};

type Config = {
  eyebrow: string;
  title: string;
  phaseDescription: string;
  intro: string;
  nextAction: PlanAction;
  /**
   * Omit `value` where there is no treatment cycle to describe yet; the metric
   * then only appears once a real cycle is scheduled.
   */
  cycleSnapshot: { label: string; value?: string };
  journey?: JourneySource;
  customSections?: CustomSection[];
};

type ViewerSexAtBirth = "AFAB" | "AMAB" | "unknown";

type ActionsMap = {
  choosePath: PlanAction;
  learn: PlanAction;
  testing: PlanAction;
  medicalHistory: PlanAction;
  care: PlanAction;
  findCare: PlanAction;
  coverage: PlanAction;
};

function buildActions(t: TranslationFunction): ActionsMap {
  return {
    choosePath: {
      label: t("homepage.guided_precycle.actions.choose_path"),
      signal: "open-intent-picker",
    },
    learn: {
      label: t("homepage.guided_precycle.actions.learn"),
      screen: "LearnScreen",
    },
    testing: {
      label: t("homepage.guided_precycle.actions.testing"),
      screen: "AtHomeFertilityTesting",
    },
    medicalHistory: {
      label: t("homepage.guided_precycle.actions.medical_history"),
      screen: "MedicalHistory",
    },
    care: {
      label: t("homepage.guided_precycle.actions.care"),
      screen: "CareOverview",
    },
    findCare: {
      label: t("homepage.guided_precycle.actions.find_care"),
      screen: "FindCareScreen",
    },
    coverage: {
      label: t("homepage.guided_precycle.actions.coverage"),
      signal: "open-coverage-status-modal",
    },
  };
}

function buildConfigs(
  t: TranslationFunction,
): Record<GuidedPreCycleConceptVariant, Config> {
  const actions = buildActions(t);
  return {
    setup: {
      eyebrow: t("homepage.guided_precycle.setup.eyebrow"),
      title: t("homepage.guided_precycle.setup.title"),
      phaseDescription: t("homepage.guided_precycle.setup.phase_description"),
      intro: t("homepage.guided_precycle.setup.intro"),
      nextAction: actions.choosePath,
      cycleSnapshot: {
        label: t("homepage.guided_precycle.setup.snapshot_cycle_label"),
      },
      customSections: [
        {
          id: "setup.path",
          stepLabel: t(
            "homepage.guided_precycle.setup.section_path_step_label",
          ),
          title: t("homepage.guided_precycle.setup.section_path_title"),
          body: t("homepage.guided_precycle.setup.section_path_body"),
          bullets: [
            t("homepage.guided_precycle.setup.section_path_bullet_preserve"),
            t("homepage.guided_precycle.setup.section_path_bullet_pregnant"),
            t("homepage.guided_precycle.setup.section_path_bullet_donate"),
          ],
          action: actions.choosePath,
        },
        {
          id: "setup.baseline",
          stepLabel: t(
            "homepage.guided_precycle.setup.section_baseline_step_label",
          ),
          title: t("homepage.guided_precycle.setup.section_baseline_title"),
          body: t("homepage.guided_precycle.setup.section_baseline_body"),
          bullets: [
            t("homepage.guided_precycle.setup.section_baseline_bullet_history"),
            t("homepage.guided_precycle.setup.section_baseline_bullet_labs"),
            t(
              "homepage.guided_precycle.setup.section_baseline_bullet_coverage",
            ),
          ],
          action: actions.medicalHistory,
        },
        {
          id: "setup.care",
          stepLabel: t(
            "homepage.guided_precycle.setup.section_care_step_label",
          ),
          title: t("homepage.guided_precycle.setup.section_care_title"),
          body: t("homepage.guided_precycle.setup.section_care_body"),
          bullets: [
            t("homepage.guided_precycle.setup.section_care_bullet_clinics"),
            t("homepage.guided_precycle.setup.section_care_bullet_programs"),
            t(
              "homepage.guided_precycle.setup.section_care_bullet_coverage_fit",
            ),
          ],
          action: actions.findCare,
        },
      ],
    },
    "learn-explorer": {
      eyebrow: t("homepage.guided_precycle.learn_explorer.eyebrow"),
      title: t("homepage.guided_precycle.learn_explorer.title"),
      phaseDescription: t(
        "homepage.guided_precycle.learn_explorer.phase_description",
      ),
      intro: t("homepage.guided_precycle.learn_explorer.intro"),
      nextAction: actions.learn,
      cycleSnapshot: {
        label: t(
          "homepage.guided_precycle.learn_explorer.snapshot_cycle_label",
        ),
      },
      customSections: [
        {
          id: "learn.options",
          stepLabel: t(
            "homepage.guided_precycle.learn_explorer.section_options_step_label",
          ),
          title: t(
            "homepage.guided_precycle.learn_explorer.section_options_title",
          ),
          body: t(
            "homepage.guided_precycle.learn_explorer.section_options_body",
          ),
          bullets: [
            t(
              "homepage.guided_precycle.learn_explorer.section_options_bullet_preservation",
            ),
            t(
              "homepage.guided_precycle.learn_explorer.section_options_bullet_pregnancy",
            ),
            t(
              "homepage.guided_precycle.learn_explorer.section_options_bullet_donor_or_carrier",
            ),
          ],
          action: actions.learn,
        },
        {
          id: "learn.numbers",
          stepLabel: t(
            "homepage.guided_precycle.learn_explorer.section_numbers_step_label",
          ),
          title: t(
            "homepage.guided_precycle.learn_explorer.section_numbers_title",
          ),
          body: t(
            "homepage.guided_precycle.learn_explorer.section_numbers_body",
          ),
          bullets: [
            t(
              "homepage.guided_precycle.learn_explorer.section_numbers_bullet_ovarian",
            ),
            t(
              "homepage.guided_precycle.learn_explorer.section_numbers_bullet_semen",
            ),
            t(
              "homepage.guided_precycle.learn_explorer.section_numbers_bullet_carrier",
            ),
          ],
          action: actions.testing,
        },
        {
          id: "learn.care",
          stepLabel: t(
            "homepage.guided_precycle.learn_explorer.section_care_step_label",
          ),
          title: t(
            "homepage.guided_precycle.learn_explorer.section_care_title",
          ),
          body: t("homepage.guided_precycle.learn_explorer.section_care_body"),
          bullets: [
            t(
              "homepage.guided_precycle.learn_explorer.section_care_bullet_clinics",
            ),
            t(
              "homepage.guided_precycle.learn_explorer.section_care_bullet_labs",
            ),
            t(
              "homepage.guided_precycle.learn_explorer.section_care_bullet_programs",
            ),
          ],
          action: actions.findCare,
        },
      ],
    },
    "trying-naturally": {
      eyebrow: t("homepage.guided_precycle.trying_naturally.eyebrow"),
      title: t("homepage.guided_precycle.trying_naturally.title"),
      phaseDescription: t(
        "homepage.guided_precycle.trying_naturally.phase_description",
      ),
      intro: t("homepage.guided_precycle.trying_naturally.intro"),
      nextAction: actions.medicalHistory,
      cycleSnapshot: {
        label: t(
          "homepage.guided_precycle.trying_naturally.snapshot_cycle_label",
        ),
      },
      customSections: [
        {
          id: "trying-naturally.preparation",
          stepLabel: t(
            "homepage.guided_precycle.trying_naturally.section_preparation_step_label",
          ),
          title: t(
            "homepage.guided_precycle.trying_naturally.section_preparation_title",
          ),
          body: t(
            "homepage.guided_precycle.trying_naturally.section_preparation_body",
          ),
          bullets: [
            t(
              "homepage.guided_precycle.trying_naturally.section_preparation_bullet_history",
            ),
            t(
              "homepage.guided_precycle.trying_naturally.section_preparation_bullet_health",
            ),
            t(
              "homepage.guided_precycle.trying_naturally.section_preparation_bullet_partner",
            ),
          ],
          action: actions.medicalHistory,
        },
        {
          id: "trying-naturally.tracking",
          stepLabel: t(
            "homepage.guided_precycle.trying_naturally.section_tracking_step_label",
          ),
          title: t(
            "homepage.guided_precycle.trying_naturally.section_tracking_title",
          ),
          body: t(
            "homepage.guided_precycle.trying_naturally.section_tracking_body",
          ),
          bullets: [
            t(
              "homepage.guided_precycle.trying_naturally.section_tracking_bullet_cycle",
            ),
            t(
              "homepage.guided_precycle.trying_naturally.section_tracking_bullet_optional",
            ),
            t(
              "homepage.guided_precycle.trying_naturally.section_tracking_bullet_estimates",
            ),
          ],
          action: actions.care,
        },
        {
          id: "trying-naturally.care",
          stepLabel: t(
            "homepage.guided_precycle.trying_naturally.section_care_step_label",
          ),
          title: t(
            "homepage.guided_precycle.trying_naturally.section_care_title",
          ),
          body: t(
            "homepage.guided_precycle.trying_naturally.section_care_body",
          ),
          bullets: [
            t(
              "homepage.guided_precycle.trying_naturally.section_care_bullet_timing",
            ),
            t(
              "homepage.guided_precycle.trying_naturally.section_care_bullet_risk",
            ),
            t(
              "homepage.guided_precycle.trying_naturally.section_care_bullet_provider",
            ),
          ],
          action: actions.findCare,
        },
      ],
    },
    "ivf-precycle": {
      eyebrow: t("homepage.guided_precycle.ivf_precycle.eyebrow"),
      title: t("homepage.guided_precycle.ivf_precycle.title"),
      phaseDescription: t(
        "homepage.guided_precycle.ivf_precycle.phase_description",
      ),
      intro: t("homepage.guided_precycle.ivf_precycle.intro"),
      nextAction: actions.testing,
      cycleSnapshot: {
        label: t("homepage.guided_precycle.ivf_precycle.snapshot_cycle_label"),
        value: t("homepage.guided_precycle.ivf_precycle.snapshot_cycle_value"),
      },
      journey: { gender: "female", goal: "pregnancy", pathway: "with-partner" },
    },
    "iui-precycle": {
      eyebrow: t("homepage.guided_precycle.iui_precycle.eyebrow"),
      title: t("homepage.guided_precycle.iui_precycle.title"),
      phaseDescription: t(
        "homepage.guided_precycle.iui_precycle.phase_description",
      ),
      intro: t("homepage.guided_precycle.iui_precycle.intro"),
      nextAction: actions.testing,
      cycleSnapshot: {
        label: t("homepage.guided_precycle.iui_precycle.snapshot_cycle_label"),
        value: t("homepage.guided_precycle.iui_precycle.snapshot_cycle_value"),
      },
      journey: { gender: "female", goal: "pregnancy", pathway: "with-partner" },
    },
    "amab-family-building-precycle": {
      eyebrow: t(
        "homepage.guided_precycle.amab_family_building_precycle.eyebrow",
      ),
      title: t("homepage.guided_precycle.amab_family_building_precycle.title"),
      phaseDescription: t(
        "homepage.guided_precycle.amab_family_building_precycle.phase_description",
      ),
      intro: t("homepage.guided_precycle.amab_family_building_precycle.intro"),
      nextAction: actions.testing,
      cycleSnapshot: {
        label: t(
          "homepage.guided_precycle.amab_family_building_precycle.snapshot_cycle_label",
        ),
        value: t(
          "homepage.guided_precycle.amab_family_building_precycle.snapshot_cycle_value",
        ),
      },
      journey: { gender: "male", goal: "pregnancy", pathway: "with-partner" },
    },
  };
}

export function GuidedPreCycleConceptHome({
  variant,
  userAge,
  viewerSexAtBirth = "unknown",
  viewerIsSupporting = false,
  onAction,
  onOpenPlan,
  onEditPlan,
  scheduledCycleStartDate,
  cycleEnding,
}: {
  variant: GuidedPreCycleConceptVariant;
  userAge?: number;
  viewerSexAtBirth?: ViewerSexAtBirth;
  /**
   * A partner reading the patient's plan. These heroes already speak about the
   * plan rather than its owner, so only the steps that are the patient's to
   * take (starting the cycle, choosing the path) are left out.
   */
  viewerIsSupporting?: boolean;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
  /** Future cycle date persisted on an open API cycle, when available. */
  scheduledCycleStartDate?: string | null;
  /**
   * A cycle that has just ended. Passed straight to the hero, which announces it
   * instead of a separate card sitting above this whole surface.
   */
  cycleEnding?: HeroCycleEnding | null;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const configs = useMemo(() => buildConfigs(t), [t]);
  const config = configs[variant];
  const journey = config.journey
    ? getJourneyForState(
        config.journey.gender,
        config.journey.goal,
        config.journey.pathway,
        variant === "iui-precycle" ? "iui" : undefined,
      )
    : null;
  const sourceSteps = journey?.steps ?? customSectionsToSteps(config);
  const steps = toPrepSteps(sourceSteps);
  const sections = usesGuidedTreatmentSections(variant)
    ? guidedTreatmentSections(
        variant,
        userAge,
        viewerSexAtBirth,
        viewerIsSupporting,
        onAction,
        t,
      )
    : variant === "learn-explorer"
      ? explorerSections(userAge, viewerSexAtBirth, onAction, t)
      : journey
        ? journeyStepsToSections(journey.steps, onAction, t)
        : customSectionsToPreCycleSections(
            config.customSections ?? [],
            onAction,
          );
  const showStartCycleAction = usesGuidedTreatmentSections(variant);
  const cycleSnapshotValue = scheduledCycleStartDate
    ? formatDate(scheduledCycleStartDate, locale)
    : config.cycleSnapshot.value;

  return (
    <PreCycleHome
      activeStep={null}
      steps={steps}
      completedSteps={0}
      totalSteps={sourceSteps.length}
      eyebrow={config.eyebrow}
      title={config.title}
      nextAction={config.nextAction}
      snapshotItems={
        cycleSnapshotValue
          ? [{ label: config.cycleSnapshot.label, value: cycleSnapshotValue }]
          : []
      }
      phaseDescription={config.phaseDescription}
      intro={config.intro}
      sections={sections}
      onAction={onAction}
      onOpenPlan={onOpenPlan}
      onEditPlan={onEditPlan}
      viewerIsSupporting={viewerIsSupporting}
      cycleEnding={cycleEnding}
      showStartCycleAction={showStartCycleAction}
      startCyclePreset={
        variant === "iui-precycle"
          ? "iui"
          : showStartCycleAction
            ? "ivf"
            : undefined
      }
    />
  );
}

function usesGuidedTreatmentSections(
  variant: GuidedPreCycleConceptVariant,
): variant is
  "ivf-precycle" | "iui-precycle" | "amab-family-building-precycle" {
  return (
    variant === "ivf-precycle" ||
    variant === "iui-precycle" ||
    variant === "amab-family-building-precycle"
  );
}

function explorerSections(
  userAge: number | undefined,
  viewerSexAtBirth: ViewerSexAtBirth,
  onAction: (action: PlanAction) => void,
  t: TranslationFunction,
): PreCycleHomeSection[] {
  const isAmab = viewerSexAtBirth === "AMAB";
  const careVariant = isAmab ? "amab-family-building" : "egg-freezing";

  return [
    {
      id: "learn.options",
      progressStepIds: ["learn"],
      render: () => (
        <ExplorerBiologyOptionsCard
          userAge={userAge}
          viewerSexAtBirth={viewerSexAtBirth}
          onAction={onAction}
        />
      ),
    },
    {
      id: "learn.numbers",
      progressStepIds: ["test"],
      render: () => (
        <EggFreezingKnowYourNumbersTimelineStepSectionCard
          onAction={onAction}
          showRail={false}
          variant="explorer"
          viewerSexAtBirth={viewerSexAtBirth}
          cardTitle={t(
            "homepage.guided_precycle.learn_explorer.section_numbers_title",
          )}
          cardSubtitle=""
        />
      ),
    },
    {
      id: "learn.care",
      progressStepIds: ["clinic"],
      render: () => (
        <EggFreezingClinicTimelineStep
          onAction={onAction}
          variant={careVariant}
        />
      ),
    },
    {
      id: "learn.handoff",
      includeInPlanSetup: false,
      // Its call to action is choosing the path, which is the patient's.
      patientOnly: true,
      progressStepIds: [],
      render: () => <ExplorerJourneyHandoffCard onAction={onAction} />,
    },
  ];
}

function guidedTreatmentSections(
  variant: "ivf-precycle" | "iui-precycle" | "amab-family-building-precycle",
  userAge: number | undefined,
  viewerSexAtBirth: ViewerSexAtBirth,
  viewerIsSupporting: boolean,
  onAction: (action: PlanAction) => void,
  t: TranslationFunction,
): PreCycleHomeSection[] {
  const isAmab = variant === "amab-family-building-precycle";
  const isIui = variant === "iui-precycle";
  const isSpermFirstIvf =
    variant === "ivf-precycle" && viewerSexAtBirth === "AMAB";
  const biologyVariant: PreCycleBiologyVariant = isIui
    ? "iui"
    : isAmab
      ? "amab-family-building"
      : "ivf";
  const treatmentVariant = isIui
    ? "iui"
    : isAmab
      ? "amab-family-building"
      : "ivf";

  return [
    {
      id: "understand",
      progressStepIds: ["learn"],
      completionStepId: `precycle-${variant}.understand`,
      render: () => (
        <UnderstandEmbryoFreezingStep
          userAge={userAge}
          onAction={onAction}
          variant={biologyVariant}
          stepId={`precycle-${variant}.understand`}
          title={
            isIui
              ? "How IUI works"
              : isAmab
                ? "How embryo creation works"
                : "How IVF and embryo creation work"
          }
          subtitle={
            isIui
              ? "Start with IUI fit, tube status, ovulation timing, sperm preparation, insemination, and the beta plan."
              : isAmab
                ? "Start with sperm inputs, donor egg or ovarian side, carrier path, then lab fertilization and embryo culture."
                : "Start with stimulation, sperm source, fertilization, embryo culture, testing or transfer."
          }
        />
      ),
    },
    {
      id: "know-your-numbers",
      progressStepIds: ["test"],
      completionStepId: `precycle-${treatmentVariant}.know-your-numbers.timeline`,
      render: () => (
        <EggFreezingKnowYourNumbersTimelineStepSectionCard
          onAction={onAction}
          showRail={false}
          variant={treatmentVariant}
          priority={isSpermFirstIvf ? "sperm-first" : "ovarian-first"}
          viewerIsSupporting={viewerIsSupporting}
        />
      ),
    },
    {
      id: "optimize",
      progressStepIds: ["prep"],
      completionStepId: `precycle-${treatmentVariant}.optimize`,
      render: () => (
        <OptimizeInputsStep onAction={onAction} variant={treatmentVariant} />
      ),
    },
    {
      id: "find-care",
      progressStepIds: ["clinic"],
      completionStepId: `precycle-${treatmentVariant}.clinic.timeline`,
      render: () => (
        <FindEmbryoCareStep onAction={onAction} variant={treatmentVariant} />
      ),
    },
    {
      id: "start-cycle",
      includeInPlanSetup: false,
      patientOnly: true,
      progressStepIds: ["treatment"],
      render: () => (
        <StartEmbryoCycleStep
          onAction={onAction}
          title={
            isIui
              ? t("homepage.review_concept.guided_iui_cycle_title")
              : isAmab
                ? t("homepage.review_concept.guided_amab_cycle_title")
                : t("homepage.review_concept.guided_ivf_cycle_title")
          }
          body={
            isIui
              ? t("homepage.review_concept.guided_iui_cycle_body_full")
              : isAmab
                ? t("homepage.review_concept.guided_amab_cycle_body")
                : t("homepage.review_concept.guided_ivf_cycle_body_full")
          }
          ctaLabel={
            isIui
              ? t("homepage.review_concept.guided_iui_cycle_cta")
              : t("homepage.review_concept.guided_ivf_cycle_cta")
          }
          presetCycleType={isIui ? "iui" : "ivf"}
          checklist={
            isIui
              ? [
                  t("homepage.review_concept.guided_iui_checklist_day1"),
                  t("homepage.review_concept.guided_iui_checklist_meds_mon"),
                  t("homepage.review_concept.guided_iui_checklist_sperm_wash"),
                  t(
                    "homepage.review_concept.guided_iui_checklist_trigger_beta",
                  ),
                ]
              : isAmab
                ? [
                    t("homepage.review_concept.guided_amab_checklist_sperm"),
                    t("homepage.review_concept.guided_amab_checklist_egg"),
                    t("homepage.review_concept.guided_amab_checklist_carrier"),
                    t("homepage.review_concept.guided_amab_checklist_lab"),
                  ]
                : [
                    t("homepage.review_concept.guided_ivf_checklist_timing_v2"),
                    t("homepage.review_concept.guided_ivf_checklist_sperm"),
                    t(
                      "homepage.review_concept.guided_ivf_checklist_fertilization",
                    ),
                    t("homepage.review_concept.guided_ivf_checklist_transfer"),
                  ]
          }
        />
      ),
    },
  ];
}

function customSectionsToSteps(config: Config): PlanStep[] {
  return (config.customSections ?? []).map((section) => ({
    id: section.id,
    title: section.title,
    subtitle: section.body,
    education: { context: section.body },
    actions: [section.action],
    completionSignal: { type: "manual" },
  }));
}

function toPrepSteps(steps: PlanStep[]): PreCyclePrepStep[] {
  return steps.map((step): PreCyclePrepStep => {
    const status: StepStatus = "not-started";
    return {
      id: step.id,
      title: step.title,
      icon: step.icon,
      status,
    };
  });
}

function journeyStepsToSections(
  steps: PlanStep[],
  onAction: (action: PlanAction) => void,
  t: TranslationFunction,
): PreCycleHomeSection[] {
  return steps
    .filter((step) => step.id !== "wait")
    .map((step, index) => ({
      id: step.id,
      progressStepIds: [step.id],
      includeInPlanSetup: step.id !== "treatment",
      render: () => (
        <GuidedStepCard
          key={step.id}
          step={step}
          stepLabel={t("homepage.guided_precycle.step_number_and_title", {
            params: { number: String(index + 1), title: step.title },
          })}
          onAction={onAction}
        />
      ),
    }));
}

function customSectionsToPreCycleSections(
  sections: CustomSection[],
  onAction: (action: PlanAction) => void,
): PreCycleHomeSection[] {
  return sections.map((section, index) => ({
    id: section.id,
    progressStepIds: [section.id],
    render: () => (
      <StepperSection
        key={section.id}
        id={`guided-concept.${section.id}`}
        stepLabel={section.stepLabel}
        showRail={index > 0}
      >
        <CardSurface style={{ padding: 18 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              lineHeight: 23,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {section.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 6,
              fontSize: 13,
              lineHeight: 19,
              fontWeight: "500",
              color: EMBER.colors.textLight,
            }}
          >
            {section.body}
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 14,
            }}
          >
            {section.bullets.map((bullet) => (
              <NeutralStepPill key={bullet} label={bullet} />
            ))}
          </View>
          <View style={{ marginTop: 16, alignSelf: "flex-start" }}>
            <CompactActionButton
              label={section.action.label}
              trailingIcon={ArrowRight}
              minWidth={168}
              onPress={() => onAction(section.action)}
            />
          </View>
        </CardSurface>
      </StepperSection>
    ),
  }));
}

function GuidedStepCard({
  step,
  stepLabel,
  onAction,
}: {
  step: PlanStep;
  stepLabel: string;
  onAction: (action: PlanAction) => void;
}) {
  const details = step.education.details
    ?.split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 4);
  const substeps = [
    ...(step.substeps ?? []),
    ...(step.partnerTrack?.substeps ?? []),
  ];

  return (
    <StepperSection
      id={`guided-concept.${step.id}`}
      stepLabel={stepLabel}
      showRail={step.id !== "learn"}
    >
      <CardSurface style={{ padding: 18 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            lineHeight: 23,
            fontWeight: "700",
            color: EMBER.colors.text,
          }}
        >
          {step.title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 4,
            fontSize: 13,
            lineHeight: 18,
            fontWeight: "700",
            color: EMBER.colors.textMid,
          }}
        >
          {step.subtitle}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 10,
            fontSize: 13,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.textLight,
          }}
        >
          {step.education.context}
        </Text>

        {details && details.length > 0 ? (
          <View style={{ gap: 8, marginTop: 14 }}>
            {details.map((detail) => (
              <Text
                key={detail}
                className="font-dm-sans"
                style={{
                  fontSize: 12.5,
                  lineHeight: 18,
                  fontWeight: "500",
                  color: EMBER.colors.textMid,
                }}
              >
                {detail}
              </Text>
            ))}
          </View>
        ) : null}

        {substeps.length > 0 ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 14,
            }}
          >
            {substeps.slice(0, 8).map((substep) => (
              <NeutralStepPill key={substep.id} label={substep.title} />
            ))}
          </View>
        ) : null}

        {step.actions.length > 0 ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 16,
            }}
          >
            {step.actions.slice(0, 2).map((action) => (
              <CompactActionButton
                key={action.label}
                label={action.label}
                trailingIcon={ArrowRight}
                variant={action.variant === "primary" ? "primary" : "secondary"}
                minWidth={action.variant === "primary" ? 168 : 118}
                onPress={() => onAction(action)}
              />
            ))}
          </View>
        ) : null}
      </CardSurface>
    </StepperSection>
  );
}
